import type { ValueOf } from './ValueOf';
import type { ApprovalStatus } from './ApprovalStatus';
import type { ApprovalType } from './ApprovalType';
import type { Actor, ApprovalActor } from './ApprovalActor';
import type { ApprovalAction } from './ApprovalAction';
import type { ApprovalRules } from './ApprovalRules';

export interface ApprovalBase {
  approvalType: ApprovalType;
  approvalAction: ApprovalAction;
  approvableObjectId: number;
}

export interface ApprovalCreate extends ApprovalBase {
  title: string;
  approverActorIds: string[];
  note: string;
  approvalRules: string;
  dueDate?: number;
}

export const ApprovalStepStatus = {
  APPROVAL_PENDING: 'APPROVAL_PENDING',
  APPROVED: 'APPROVED',
  CHANGES_REQUESTED: 'CHANGES_REQUESTED',
  REJECTED: 'REJECTED',
  WAITING: 'WAITING',
} as const;

export type ApprovalStepStatus = ValueOf<typeof ApprovalStepStatus>;

export const AgentTurnType = {
  REVIEW: 'REVIEW',
  APPLY_CHANGES: 'APPLY_CHANGES',
} as const;

export type AgentTurnType = ValueOf<typeof AgentTurnType>;

// The operations the current viewer may take on a given step, as computed and
// returned by the backend per step in getLatestApproval. Mirrors the generated
// CHIRP ApprovalStepOperation union (declared locally because the common package
// cannot import the app's generated CHIRP types, same as ApprovalStepStatus).
export const ApprovalStepOperation = {
  APPROVE: 'APPROVE',
  REQUEST_CHANGES: 'REQUEST_CHANGES',
  REJECT: 'REJECT',
} as const;

export type ApprovalStepOperation = ValueOf<typeof ApprovalStepOperation>;

export interface ApprovalStep {
  // Resolved name of the user who acted on this step (approved, rejected, or
  // requested changes). Only set for TEAM steps once a member acts; absent for
  // USER steps and for TEAM steps acted on before the backend recorded it.
  actedByDisplayName?: string;
  approvalStepStatus: ApprovalStepStatus;
  approvedNote: string;
  approver: ApprovalActor;
  availableOperations?: ApprovalStepOperation[];
  changesRequestedNote: string | null;
  conversationId?: number;
  createDateTimestamp: number;
  id: number;
  inProgressTurnType?: AgentTurnType;
  order?: number;
  rejectionNote: string | null;
  reviewTurnId?: number;
}

export interface ApprovalStepDraft {
  id: string;
  approvers: ApprovalActor[];
  templateStageLocked?: boolean;
}

export interface ApprovalStepInput {
  actor: Actor;
  order?: number;
}

export type ApprovalConclusionReason =
  | null
  | 'ACTION_TAKEN'
  | 'APPROVALS_DISABLED'
  | 'REQUESTER_RECALLED_APPROVAL'
  | 'APPROVER_REJECTED';

export type ApprovalData = {
  approvalAction: ApprovalAction;
  approvalConcluded: null | boolean;
  approvalConclusionReason: ApprovalConclusionReason;
  approvalRules: ApprovalRules;
  approvalStatus: ApprovalStatus;
  approvalSteps: ApprovalStep[];
  approvalType: ApprovalType;
  cancelNote: string | null;
  cancelNoteTimestamp: number | null;
  createDateTimestamp: number;
  dueDate: number | null;
  id: number;
  portalId: number;
  requester: ApprovalActor;
  requestNote: string;
  title: string;
} | null;

export type BulkApprovalsData = {
  createdApprovals: ApprovalData[];
  failedApprovals: ApprovalCreate[];
};

export interface ApprovalLatest {
  approval: ApprovalData;
}
